import { Navigate, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { logoutProvider, logoutUser, patientTempLogin } from "../../store/actions/account.action";
import { StateParams } from "../../store/reducers";
import { MixpanelContext } from "../../analytics/Mixpanel";
import FrontDeskIllustration from "../../assets/images/front_desk_illustration.png";
import {
    clearAllData,
    createProfileViaCheckInFlow,
    setAppointmentId,
    verifyCheckIn
} from "../../store/actions/signup.action";
import { useTranslation } from 'react-i18next'
import { useFlags } from "flagsmith/react";
import InputComponent from "../generic/InputComponent";
import RadioButtonGroupComponentComponent from "../generic/RadioButtonGroupComponent";
import { ICheckInFlowCreatePatientRequest, LanguageCodes } from "../../models";

const SELF_CHECK_IN_SOURCE = "self-check-in-link";

interface CheckInScreeningEntryScreenProps {
    loginSource?: string;
}

const CheckInScreeningEntryScreen = ({ loginSource = SELF_CHECK_IN_SOURCE }: CheckInScreeningEntryScreenProps) => {
    const { t, i18n } = useTranslation();
    const analytics = React.useContext(MixpanelContext);
    const flags = useFlags(['enable_new_patient_form']);
    const isNewPatientFormEnabled = flags.enable_new_patient_form?.enabled ?? false;
    const {
        checkInId,
    } = useParams();
    const {
        jwt,
        signInError,
    } = useSelector((state: StateParams) => state.account);
    const {
        isCheckInVerificationInProgress,
        isCheckInVerificationSuccess,
        checkInVerificationResponse,
        checkInVerificationError,
        createProfileViaCheckInFlowResponse,
        createProfileViaCheckInFlowError,
    } = useSelector((state: StateParams) => state.signup);

    const [readyForScreening, setReadyForScreening] = useState<boolean>(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        gender: ""
    });

    const mapLanguageCode = (preferredLanguage: string): string => {
        const languageMap: { [key: string]: string } = {
            'en_us': 'en',
            'ru': 'ru',
            'es': 'es',
            'ar': 'ar',
            'ur': 'ur',
        };
        return languageMap[preferredLanguage.toLowerCase()] || preferredLanguage;
    };

    const shouldShowNewPatientForm =
        isNewPatientFormEnabled &&
        (
            loginSource === "practice-connect-patient-form" ||
            (
                checkInVerificationResponse?.demographicsMatchedPatientCount === 1 &&
                checkInVerificationResponse?.appointment?.isNewVisit === true &&
                checkInVerificationResponse?.hasScreeningSession === false
            )
        );

    const enableScreeningForSinglePatientMatch = checkInVerificationResponse && checkInVerificationResponse?.hasScreeningSession || (!checkInVerificationResponse?.appointment || checkInVerificationResponse.appointment.isNewVisit === false);


    useEffect(() => {
        setReadyForScreening(false)
        localStorage.removeItem("sj:patient-session-id")
        Object.keys(sessionStorage).filter(k => k.startsWith('pf_')).forEach(k => sessionStorage.removeItem(k))
        console.log("CSES: LOGOUT USER")
        dispatch(logoutUser())
        console.log("CSES: LOGOUT PROVIDER")
        dispatch(logoutProvider())
        console.log("CSES: CLEAR ALL DATA")
        dispatch(clearAllData())
    }, [dispatch]);

    useEffect(() => {
        if (checkInVerificationResponse?.patientCheckInRecord?.preferredLanguage) {
            const languageCode = mapLanguageCode(checkInVerificationResponse.patientCheckInRecord.preferredLanguage);
            i18n.changeLanguage(languageCode);
        }
    }, [checkInVerificationResponse, i18n]);

    useEffect(() => {
        if (createProfileViaCheckInFlowResponse?.patientCheckInRecord?.preferredLanguage) {
            const languageCode = mapLanguageCode(createProfileViaCheckInFlowResponse.patientCheckInRecord.preferredLanguage);
            i18n.changeLanguage(languageCode);
        }
    }, [createProfileViaCheckInFlowResponse, i18n]);


    useEffect(() => {
        if (
            checkInVerificationResponse?.patient &&
            checkInVerificationResponse.patientCheckInRecord &&
            checkInVerificationResponse.demographicsMatchedPatientCount === 1 &&
            enableScreeningForSinglePatientMatch
        ) {
            console.log("CheckInScreeningEntryScreen: Single patient match found, dispatching patientTempLogin");
            dispatch(patientTempLogin({
                practiceId: checkInVerificationResponse.patientCheckInRecord.practiceId,
                patientId: checkInVerificationResponse.patient.id,
                providerId: checkInVerificationResponse.patientCheckInRecord.providerId,
                locationId: checkInVerificationResponse.patientCheckInRecord.locationId,
                appointmentId: checkInVerificationResponse.appointment?.id,
                loginSource,
            }));
            if (checkInVerificationResponse.appointment?.id) {
                dispatch(setAppointmentId({ appointmentId: checkInVerificationResponse.appointment.id }));
            }
            setReadyForScreening(true);
        }
    }, [dispatch, checkInVerificationResponse, enableScreeningForSinglePatientMatch, loginSource]);

    useEffect(() => {
        const patient = createProfileViaCheckInFlowResponse?.patient;
        const checkInRecord = createProfileViaCheckInFlowResponse?.patientCheckInRecord;
        console.log("CheckInScreeningEntryScreen: createProfileViaCheckInFlowResponse changed", createProfileViaCheckInFlowResponse);
        if (patient && checkInRecord) {
            dispatch(patientTempLogin({
                practiceId: checkInRecord.practiceId,
                patientId: patient.id,
                providerId: checkInRecord.providerId,
                locationId: checkInRecord.locationId,
                loginSource,
            }));
            setReadyForScreening(true);
        }
    }, [dispatch, createProfileViaCheckInFlowResponse, loginSource]);

    useEffect(() => {
        const patient = createProfileViaCheckInFlowResponse?.patient ?? checkInVerificationResponse?.patient;
        const checkInRecord = createProfileViaCheckInFlowResponse?.patientCheckInRecord ?? checkInVerificationResponse?.patientCheckInRecord;

        if (jwt && readyForScreening && patient && checkInRecord && !signInError) {
            console.log("sending mixpanel analytics for opened_screener_link_via_sms");

            const trackEvent = new Promise((resolve) => {
                analytics?.identify(patient.id);
                analytics?.track("opened_screener_link_via_sms", {
                    practiceId: checkInRecord.practiceId,
                    patientId: patient.id
                }, resolve);
            });

            const timeout = new Promise((resolve) =>
                setTimeout(() => {
                    console.warn("Mixpanel tracking timed out; proceeding to /patientProfile.");
                    resolve(null);
                }, 500)
            );

            Promise.race([trackEvent, timeout]).then(() => {
                navigate('/screenerQuestion')
            });
        }
    }, [navigate, signInError, jwt, readyForScreening, analytics, checkInVerificationResponse, createProfileViaCheckInFlowResponse]);

    useEffect(() => {
        if (checkInId) {
            dispatch(verifyCheckIn({ checkInId: checkInId }));
        } else {
            console.error("CheckInScreeningEntryScreen: No checkInId found in params");
        }
    }, [checkInId, dispatch]);

    const onChangeValue = (id: string, value: string) => {
        setFormData(prevState => ({
            ...prevState,
            [id]: value
        }));
    };

    const onSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
        if (checkInId) {
            e.preventDefault();
            console.log("onSubmit called with formData: ", formData);
            const patientData: ICheckInFlowCreatePatientRequest = {
                ...formData,
                preferredLanguage: checkInVerificationResponse ? checkInVerificationResponse.patientCheckInRecord.preferredLanguage : LanguageCodes.EN_US
            };
            dispatch(createProfileViaCheckInFlow({
                checkInId: checkInId,
                patientData: patientData
            }));
        }
    }, [checkInId, formData, dispatch, checkInVerificationResponse]);

    const renderNoPatientFound = useCallback(() => {
        return (
            <form onSubmit={onSubmit} className={'px-6 py-8 mx-4 bg-white rounded-2xl w-full md:w-2/3 lg:w-1/4'}>
                <div>
                    <h1 className={'text-2xl font-bold'}>{t('tellUsAboutYourself', 'Tell us about yourself')}</h1>
                    <p className={'text-sm text-gray-600'}>{t('fewDetailsToGetStarted', 'Just a few details to get started')}</p>
                </div>
                <div className={'mt-3'}>
                    <InputComponent
                        label={t('firstName', 'First Name') || 'First Name'}
                        name='firstName'
                        onChange={(e) => onChangeValue('firstName', e.target.value)}
                        value={formData.firstName}
                        placeholder={t('enterFirstName', 'Enter your first name') || 'Enter your first name'} />
                </div>
                <div className={'mt-3'}>
                    <InputComponent
                        label={t('lastName', 'Last Name') || 'Last Name'}
                        name='lastName'
                        onChange={(e) => onChangeValue('lastName', e.target.value)}
                        value={formData.lastName}
                        placeholder={t('enterLastName', 'Enter your last name') || 'Enter your last name'} />
                </div>
                <div className={'mt-3'}>
                    <RadioButtonGroupComponentComponent
                        label={t('gender', 'Gender') || 'Gender'}
                        options={[
                            { 'label': t('male', 'Male') || 'Male', value: 'male' },
                            { 'label': t('female', 'Female') || 'Female', value: 'female' }
                        ]}
                        onChange={(e => onChangeValue('gender', e.target.value))}
                        value={formData.gender}
                        name={'gender'}
                    />
                </div>
                <button data-test="proceed"
                    className='bg-orange text-white font-bold p-2 rounded-xl w-full mt-4' type='submit'
                    value="Submit">
                    {t('proceedButton', 'Proceed')}
                </button>
                {createProfileViaCheckInFlowError && <span
                    className='text-sm text-center text-red-700 rounded-lg dark:text-red-800'>{createProfileViaCheckInFlowError}</span>}
            </form>
        );
    }, [onSubmit, formData, onChangeValue, createProfileViaCheckInFlowError, t]);

    const renderSinglePatientFound = useCallback(() => {
        return (
            <div className="text-center">
                <h1>{t('welcomeBack', 'Welcome back! You have successfully checked in.')}</h1>
                <p>{t('detailsVerified', 'Your details have been verified.')}</p>
                <p>{t('proceedingToScreening', 'Proceeding to Screening Process')}</p>
            </div>
        );
    }, [t]);

    const renderMultiplePatientsFound = useCallback(() => {
        return (
            <div className="flex flex-col items-center gap-5">
                <img src={FrontDeskIllustration} alt={'FrontDeskIllustration'} height={200} width={200} />
                <p className="text-gray-700 text-center whitespace-pre-line">
                    {t(
                        'multipleRecordsFound',
                        'Welcome to Our Practice!\nPlease check in at the front desk first. Your assessment will be completed in the patient room.'
                    )}
                </p>
            </div>
        );
    }, [t]);

    return (
        <div className={"static-full-screen bg-[#fcf3f2] items-center"}>
            {isCheckInVerificationInProgress && <div>{t('verifyingCheckIn', 'Verifying check-in, it may take a few seconds...')}</div>}
            {isCheckInVerificationSuccess && <>
                {
                    checkInVerificationResponse && <>
                        {shouldShowNewPatientForm ? (
                            <Navigate
                                to={`/patient-forms/${checkInVerificationResponse.patient?.id}/create`}
                                replace
                            />
                        ) : (
                            <>
                                {checkInVerificationResponse.demographicsMatchedPatientCount === 0 &&
                                    renderNoPatientFound()}
                                {checkInVerificationResponse.demographicsMatchedPatientCount === 1 &&
                                    renderSinglePatientFound()}
                                {checkInVerificationResponse.demographicsMatchedPatientCount > 1 &&
                                    renderMultiplePatientsFound()}
                            </>
                        )}
                    </>
                }
            </>
            }
            <div className={'text-center'}>
                {
                    (signInError || checkInVerificationError) &&
                    <h1>{t('invalidScreenerLink', 'Screener Link is invalid, please contact your provider')}</h1>
                }
            </div>
        </div>
    );
}

export default CheckInScreeningEntryScreen;
